import { useContext, useState } from 'react';
import { Box, Divider, Icon, useResponsive, Text } from '@bitrise/bitkit';

import { HEADER_HEIGHT, NAV_WIDTH, NAV_WIDTH_OPEN } from '@/constants';
import { GlobalProps } from '@/models/GlobalProps';
import { releaseManagementConnectedAppsPagePath } from '@/utils/monolithApiRouteService';
import { GlobalPropsContext } from '../../GlobalPropsContext';
import WorkspaceSelector from './WorkspaceSelector/WorkspaceSelector';
import NavButton from './NavButton';

type PlatformNavProps = {
  isOpen: boolean;
  marginTop: string;
  onOpenChange: (isOpen: boolean) => void;
};
const PlatformNav = ({ isOpen, marginTop, onOpenChange }: PlatformNavProps): JSX.Element => {
  const [workspaceSelectorVisible, setWorkspaceSelectorVisible] = useState(false);
  const [shouldClose, setShouldClose] = useState(false);
  const {
    account,
    featureFlags: { account: accountFeatureFlags },
  } = useContext(GlobalPropsContext) as GlobalProps;

  const { isMobile } = useResponsive();

  const mobilePropsFoMainBox = isMobile
    ? {
        top: 0,
        background: 'rgba(34, 34, 34, 0.8)',
        width: 'auto',
        right: isOpen ? 0 : 'auto',
        left: isOpen ? 0 : '-100%',
        opacity: isOpen ? 1 : 0,
        transition: 'left .2s ease-out, opacity .25s ease-out',
      }
    : {};

  const mobilePropsForInnerBox = isMobile
    ? {
        width: '320px',
        maxWidth: 'calc(100vw - 56px)',
        height: '100%',
        background: 'neutral.100',
      }
    : {};

  return (
    <Box
      as="nav"
      className="platform-nav"
      zIndex={300}
      position="fixed"
      display="flex"
      overflow="hidden"
      background="purple.10"
      width={isOpen ? NAV_WIDTH_OPEN : NAV_WIDTH}
      transition="0.1s width"
      transitionDelay={isOpen ? '0.1s' : '0.05s'}
      top={HEADER_HEIGHT}
      left={0}
      bottom={0}
      marginTop={marginTop}
      _hover={
        isMobile
          ? {}
          : {
              width: NAV_WIDTH_OPEN,
            }
      }
      onMouseEnter={() => {
        if (isMobile) {
          return;
        }
        onOpenChange(true);
        setShouldClose(false);
      }}
      onMouseLeave={() => {
        if (isMobile) {
          return;
        }
        if (!workspaceSelectorVisible) {
          onOpenChange(false);
        } else {
          setShouldClose(true);
        }
      }}
      {...mobilePropsFoMainBox}
    >
      <Box {...mobilePropsForInnerBox}>
        {!account && <NavButton href="/create-workspace" iconName="PlusCircle" title="Create workspace" />}
        {account && (
          <NavButton
            className="platform-nav-workspace"
            height={['auto', NAV_WIDTH]}
            opacity={1}
            borderStyle="solid"
            borderBottomWidth="1px"
            borderBottomColor={['neutral.90', 'rgba(255, 255, 255, 0.2)']}
            _hover={{ background: ['none', 'rgba(255, 255, 255, .1)'], opacity: 1 }}
            _active={{ background: ['none', 'rgba(255, 255, 255, .2)'], opacity: 1 }}
            paddingX="0"
            paddingY={[16, 0]}
            marginBottom={[16, 0]}
          >
            <WorkspaceSelector
              isOpen={workspaceSelectorVisible}
              onOpen={() => setWorkspaceSelectorVisible(true)}
              onClose={() => {
                setWorkspaceSelectorVisible(false);
                if (shouldClose) {
                  onOpenChange(false);
                  setShouldClose(false);
                }
              }}
            />
          </NavButton>
        )}
        {account && accountFeatureFlags?.projectDashboard && (
          <NavButton
            href={`/workspaces/${account.slug}`}
            match="full"
            iconName="Dashboard"
            iconActiveName="DashboardFilled"
            title="Dashboard"
          />
        )}
        {account && (
          <NavButton href="/dashboard" iconName="BuildTest" iconActiveName="BuildTestSolid" title="Bitrise CI" />
        )}
        {account && (
          <NavButton href="/build-cache" iconName="BuildCache" iconActiveName="BuildCacheSolid" title="Build Cache" />
        )}
        {account && (
          <NavButton
            href={releaseManagementConnectedAppsPagePath(account.slug)}
            iconName="Release"
            iconActiveName="ReleaseSolid"
            title="Releases"
          />
        )}
        {account && accountFeatureFlags?.buildInsightsEnabled && (
          <NavButton href="/insights" iconName="Insights" iconActiveName="InsightsSolid" title="Insights" />
        )}
        {account && account.isMember && (
          <NavButton
            className="platform-nav-settings"
            href={`/workspaces/${account.slug}/settings`}
            iconName="Settings"
            iconActiveName="SettingsSolid"
            title="Settings"
          />
        )}
        {isMobile && (
          <>
            <Divider marginY="24" />
            <Text
              fontWeight="bold"
              size="1"
              textTransform="uppercase"
              color="neutral.60"
              paddingX="24"
              marginBottom="8"
            >
              Help
            </Text>
            {account && <NavButton href="/getting-started" iconName="Deployment" title="Getting Started" />}
            <NavButton href="https://support.bitrise.io/hc/en-us" iconName="Support" title="Support Center" />
          </>
        )}
      </Box>
      {isMobile && isOpen && (
        <Box onClick={() => onOpenChange(false)}>
          <Icon marginTop={account ? '36' : '12'} marginLeft="18" name="Cross" cursor="pointer" color="neutral.100" />
        </Box>
      )}
    </Box>
  );
};

export default PlatformNav;
